import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, RotateCcw, RefreshCw, Check, Move } from 'lucide-react';

export interface ImageCropperModalProps {
  isOpen: boolean;
  imageFile: File | null;
  onClose: () => void;
  onCropComplete: (croppedFile: File, previewUrl: string) => void;
  cropShape?: 'circle' | 'rounded-square';
  title?: string;
  outputSize?: number; // e.g. 512
}

export const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  isOpen,
  imageFile,
  onClose,
  onCropComplete,
  cropShape = 'circle',
  title = 'Crop Image',
  outputSize = 512,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageObj, setImageObj] = useState<HTMLImageElement | null>(null);

  // Transformations
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const VIEWPORT_SIZE = 280;

  // Load selected file into an Image object
  useEffect(() => {
    if (!imageFile) {
      setImageObj(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageObj(img);
      setZoom(1);
      setRotation(0);
      setPan({ x: 0, y: 0 });
    };
    img.src = objectUrl;

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  // Draw crop viewport and transformed image
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageObj) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = VIEWPORT_SIZE;
    const height = VIEWPORT_SIZE;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, width, height);

    // Save context for image drawing
    ctx.save();

    // Move to center of canvas
    ctx.translate(width / 2 + pan.x, height / 2 + pan.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Calculate base scale to fit the image inside viewport
    const scale = Math.max(width / imageObj.width, height / imageObj.height);
    const drawWidth = imageObj.width * scale;
    const drawHeight = imageObj.height * scale;

    ctx.drawImage(
      imageObj,
      -drawWidth / 2,
      -drawHeight / 2,
      drawWidth,
      drawHeight
    );

    ctx.restore();

    // Dark overlay with cutout mask
    ctx.save();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.65)';
    ctx.fillRect(0, 0, width, height);

    // Cutout hole
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    const radius = width / 2 - 12;
    const center = width / 2;

    if (cropShape === 'circle') {
      ctx.arc(center, center, radius, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Rounded square cutout
      const cornerRadius = 32;
      const x = 12;
      const y = 12;
      const w = width - 24;
      const h = height - 24;
      ctx.roundRect(x, y, w, h, cornerRadius);
      ctx.fill();
    }

    ctx.restore();

    // Draw viewport border & grid
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.beginPath();

    if (cropShape === 'circle') {
      ctx.arc(center, center, radius, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      const cornerRadius = 32;
      const x = 12;
      const y = 12;
      const w = width - 24;
      const h = height - 24;
      ctx.roundRect(x, y, w, h, cornerRadius);
      ctx.stroke();
    }

    // Grid lines for framing guide
    if (isDragging) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.setLineDash([4, 4]);
      
      const oneThird = width / 3;
      const twoThird = (width / 3) * 2;

      ctx.beginPath();
      ctx.moveTo(oneThird, 12);
      ctx.lineTo(oneThird, height - 12);
      ctx.moveTo(twoThird, 12);
      ctx.lineTo(twoThird, height - 12);
      ctx.moveTo(12, oneThird);
      ctx.lineTo(width - 12, oneThird);
      ctx.moveTo(12, twoThird);
      ctx.lineTo(width - 12, twoThird);
      ctx.stroke();
    }

    ctx.restore();
  }, [imageObj, zoom, rotation, pan, isDragging, cropShape]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Pointer drag handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch drag handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1 && e.touches[0]) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDragging || e.touches.length !== 1 || !e.touches[0]) return;
    const touch = e.touches[0];
    setPan({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Wheel zoom handler
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 1), 3.5));
  };

  // Rotate handlers
  const handleRotate = (direction: 'cw' | 'ccw') => {
    setRotation((prev) => (direction === 'cw' ? (prev + 90) % 360 : (prev - 90 + 360) % 360));
  };

  // Reset handler
  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  // Final high-res crop generation
  const handleApplyCrop = async () => {
    if (!imageObj) return;

    try {
      setIsProcessing(true);

      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = outputSize;
      offscreenCanvas.height = outputSize;
      const offCtx = offscreenCanvas.getContext('2d');
      if (!offCtx) return;

      offCtx.imageSmoothingEnabled = true;
      offCtx.imageSmoothingQuality = 'high';

      // Ratio between export output and preview viewport
      const ratio = outputSize / (VIEWPORT_SIZE - 24);

      // Move to center of high-res canvas
      offCtx.translate(
        outputSize / 2 + pan.x * ratio,
        outputSize / 2 + pan.y * ratio
      );
      offCtx.rotate((rotation * Math.PI) / 180);
      offCtx.scale(zoom, zoom);

      const scale = Math.max(
        VIEWPORT_SIZE / imageObj.width,
        VIEWPORT_SIZE / imageObj.height
      );
      const drawWidth = imageObj.width * scale * ratio;
      const drawHeight = imageObj.height * scale * ratio;

      offCtx.drawImage(
        imageObj,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight
      );

      offscreenCanvas.toBlob(
        (blob) => {
          if (!blob) {
            setIsProcessing(false);
            return;
          }

          const originalName = imageFile?.name || 'cropped-image.png';
          const dotIndex = originalName.lastIndexOf('.');
          const baseName = dotIndex !== -1 ? originalName.substring(0, dotIndex) : originalName;
          const fileName = `${baseName}-cropped.png`;

          const croppedFile = new File([blob], fileName, { type: 'image/png' });
          const previewUrl = URL.createObjectURL(blob);

          setIsProcessing(false);
          onCropComplete(croppedFile, previewUrl);
          onClose();
        },
        'image/png',
        0.95
      );
    } catch (err) {
      console.error('Failed to crop image:', err);
      setIsProcessing(false);
    }
  };

  if (!isOpen || !imageFile) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 select-none">
      <div className="w-full max-w-sm bg-card border border-border rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-bold text-sm text-foreground">{title}</h3>
            <p className="text-[11px] text-muted-foreground">Drag to reposition, slider to zoom</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition cursor-pointer"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Viewport Canvas Container */}
        <div className="p-4 flex flex-col items-center bg-muted/20">
          <div className="relative rounded-2xl overflow-hidden shadow-inner border border-border bg-slate-950 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE }}
              className="cursor-grab active:cursor-grabbing touch-none block"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onWheel={handleWheel}
            />

            {/* Reposition Hint Overlay */}
            <div className="absolute top-2.5 left-2.5 pointer-events-none bg-black/50 backdrop-blur-md px-2 py-0.5 rounded-full border border-white/10 text-[10px] text-white/80 font-medium flex items-center gap-1">
              <Move className="h-2.5 w-2.5" />
              <span>Pan / Drag</span>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="w-full mt-4 space-y-3">
            
            {/* Zoom Slider */}
            <div className="flex items-center gap-2.5 px-2">
              <button
                type="button"
                onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
                className="p-1 text-muted-foreground hover:text-foreground transition cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </button>

              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-primary h-1.5 bg-muted rounded-lg cursor-pointer"
              />

              <button
                type="button"
                onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}
                className="p-1 text-muted-foreground hover:text-foreground transition cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </button>

              <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">
                {Math.round(zoom * 100)}%
              </span>
            </div>

            {/* Action Buttons: Rotate & Reset */}
            <div className="flex items-center justify-center gap-2 pt-1 border-t border-border/50">
              <button
                type="button"
                onClick={() => handleRotate('ccw')}
                className="px-2.5 py-1.5 rounded-xl bg-muted/60 hover:bg-muted text-foreground border border-border text-xs font-semibold inline-flex items-center gap-1 transition cursor-pointer"
                title="Rotate 90° counter-clockwise"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>-90°</span>
              </button>

              <button
                type="button"
                onClick={() => handleRotate('cw')}
                className="px-2.5 py-1.5 rounded-xl bg-muted/60 hover:bg-muted text-foreground border border-border text-xs font-semibold inline-flex items-center gap-1 transition cursor-pointer"
                title="Rotate 90° clockwise"
              >
                <RotateCw className="h-3.5 w-3.5" />
                <span>+90°</span>
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="px-2.5 py-1.5 rounded-xl bg-muted/60 hover:bg-muted text-muted-foreground hover:text-foreground border border-border text-xs font-semibold inline-flex items-center gap-1 transition cursor-pointer ml-auto"
                title="Reset crop frame"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-border flex items-center justify-end gap-2 bg-card">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:bg-muted transition cursor-pointer disabled:opacity-50"
          >
            Cancel
          </button>
          
          <button
            type="button"
            onClick={handleApplyCrop}
            disabled={isProcessing}
            className="px-4 py-2 bg-primary text-primary-foreground text-xs font-bold rounded-xl hover:bg-primary/90 transition shadow-sm inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
            <span>{isProcessing ? 'Processing...' : 'Apply & Save'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};
